import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import SEO, { SITE_URL } from "@/components/SEO";
import { api } from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function InsightDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/insights/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-32">
        <h1 className="font-display text-3xl uppercase">Beitrag nicht gefunden</h1>
        <Link to="/insights" className="btn-secondary mt-8 inline-flex">
          <ArrowLeft size={16} strokeWidth={1.5} /> Zurück zu PINE Insights
        </Link>
      </section>
    );
  }
  if (!post) return <section className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-[#AEB7B7]">Lädt…</section>;

  const url = `${SITE_URL}/insights/${post.slug}`;

  return (
    <>
      <SEO
        title={post.meta_title || `${post.title} | PINE Insights`}
        description={post.meta_description || post.excerpt}
        canonical={url}
        ogImage={post.cover_image_url || undefined}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          datePublished: post.published_at,
          dateModified: post.updated_at,
          author: { "@type": "Organization", name: post.author || "PINE Systems" },
          publisher: { "@type": "Organization", name: "PINE Systems" },
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          ...(post.cover_image_url ? { image: post.cover_image_url } : {}),
          articleSection: post.category,
        }}
      />

      <article className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 pt-28 pb-12 md:pt-36">
          <Link to="/insights" className="overline inline-flex items-center gap-2 hover:text-white transition-colors">
            <ArrowLeft size={14} strokeWidth={1.5} /> PINE Insights
          </Link>
          <div className="overline mt-6 text-[#18E096]" data-testid="insight-category">
            {post.category}
          </div>
          <h1
            className="font-display text-3xl sm:text-4xl md:text-5xl uppercase mt-4 leading-[1.08]"
            data-testid="insight-title"
          >
            {post.title}
          </h1>
          <div className="text-[#AEB7B7] text-sm mt-6">
            {post.author || "PINE Systems"} · {formatDate(post.published_at)}
          </div>
          {post.cover_image_url ? (
            <img src={post.cover_image_url} alt="" className="w-full mt-10 border border-[rgba(255,255,255,0.08)]" />
          ) : null}
        </div>

        <div className="max-w-3xl mx-auto px-6 md:px-12 pb-20">
          <p className="text-[#F5F7F7] text-lg leading-relaxed border-l-2 border-[#18E096] pl-5 mb-10">
            {post.excerpt}
          </p>
          <div
            className="prose prose-invert max-w-none text-[#D1D7D7] text-base leading-relaxed whitespace-pre-line"
            data-testid="insight-body"
          >
            {post.body}
          </div>
        </div>
      </article>

      {/* Standard-CTA */}
      <section className="border-b border-[rgba(255,255,255,0.08)] bg-[#0B0F10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="overline mb-4">Nächster Schritt</div>
          <h2 className="font-display text-2xl md:text-4xl uppercase max-w-3xl leading-[1.1]">
            Sie möchten aus <span className="text-[#18E096]">Fläche, Inhalt oder Idee</span> ein System machen?
          </h2>
          <p className="text-[#AEB7B7] mt-6 max-w-3xl text-base leading-relaxed">
            PINE Systems prüft Standortpotenzial, Produktfit und nächste Schritte für
            Erlebnisstandorte, digitale Markenräume und professionelle LBE-Systeme.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/standort-pruefen" className="btn-primary" data-testid="insight-cta-standort">
              Standortpotenzial prüfen <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
            <Link to="/gruenden" className="btn-secondary" data-testid="insight-cta-gruenden">
              Gründer-Erstgespräch anfragen
            </Link>
            {post.cta_url && post.cta_text ? (
              <Link to={post.cta_url} className="btn-secondary">
                {post.cta_text}
              </Link>
            ) : null}
          </div>
        </div>
      </section>
    </>
  );
}
